"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const expectList = ["Detailed site assessment", "Material specification review", "Professional installation timeline", "Transparent project estimation"];
const timeSlots = ["09:00 – 10:00", "10:30 – 11:30", "13:00 – 14:00", "14:30 – 15:30", "16:00 – 17:00"];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 rounded-lg border border-border bg-card">
        <p className="font-default text-2xl text-foreground mb-3">Request received.</p>
        <p className="text-muted-foreground font-serif italic mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-sm md:text-xs font-default text-muted-foreground">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Your Name" autoComplete="name" className="w-full h-12 md:h-14 bg-background border-b-2 border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary text-sm md:text-base" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email_address" rules={{
      validate: val => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) || "Invalid email address"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-sm md:text-xs font-default text-muted-foreground">
              Email Address (optional)
            </FormLabel>
            <FormControl>
              <Input type="email" inputMode="email" autoComplete="email" placeholder="Your Email" className="w-full h-12 md:h-14 bg-background border-b-2 border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary text-sm md:text-base" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone_number" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-sm md:text-xs font-default text-muted-foreground">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" inputMode="tel" autoComplete="tel" placeholder="Your Phone Number" className="w-full h-12 md:h-14 bg-background border-b-2 border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary text-sm md:text-base" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="postal_code" rules={{
      required: "Postal code is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-sm md:text-xs font-default text-muted-foreground">
              Postal Code
            </FormLabel>
            <FormControl>
              <Input placeholder="Your Postal Code" inputMode="text" autoCapitalize="characters" autoComplete="postal-code" className="w-full h-12 md:h-14 bg-background border-b-2 border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary text-sm md:text-base" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_type" rules={{
      required: "Service needed is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-sm md:text-xs font-default text-muted-foreground">
              Service Needed
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-12 md:h-14 w-full bg-background border-b-2 border-border text-foreground text-sm md:text-base">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  {["Roofing", "Siding", "Roofing & Siding", "Other"].map((option, i) => <SelectItem key={option} data-index={i} value={option}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_description" render={({
      field
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-sm md:text-xs font-default text-muted-foreground">
              Project Details (optional)
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Describe your exterior renovation needs and current property condition." className="w-full bg-background border-b-2 border-border text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary min-h-32 md:min-h-40 text-sm md:text-base" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 gap-6">
        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-sm md:text-xs font-default text-muted-foreground">
                Preferred Date (optional)
              </FormLabel>
              <FormControl>
                <Input type="date" className="w-full h-12 md:h-14 bg-background border-b-2 border-border text-foreground focus-visible:ring-0 focus-visible:border-primary text-sm md:text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_time" render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-sm md:text-xs font-default text-muted-foreground">
                Preferred Time (optional)
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="h-12 md:h-14 w-full bg-background border-b-2 border-border text-foreground text-sm md:text-base">
                    <SelectValue placeholder="Choose a time" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg">
                    {timeSlots.map((slot, i) => <SelectItem key={slot} data-index={i} value={slot}>
                        {slot}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="supporting_document" render={({
      field: {
        onChange,
        value,
        ...rest
      }
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-sm md:text-xs font-default text-muted-foreground">
              Site Photos (optional)
            </FormLabel>
            <FormControl>
              <Input type="file" className="w-full h-12 md:h-14 bg-background border-b-2 border-border text-foreground file:text-foreground focus-visible:ring-0 focus-visible:border-primary py-2 md:py-3 text-sm md:text-base" onChange={e => onChange(e.target.files?.[0]?.name ?? '')} {...rest} />
            </FormControl>
            <FormDescription className="font-serif italic text-muted-foreground/80">
              Upload images of your current exterior (JPG, PNG, max 5 MB).
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="flex items-start gap-3 pt-2">
        <Checkbox id="nda-acknowledge" className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
        <Label htmlFor="nda-acknowledge" className="font-serif italic text-sm text-muted-foreground leading-relaxed">
          I agree to receive a professional consultation regarding my exterior renovation project.
        </Label>
      </div>

      {submitError && <div className="p-4 rounded-lg border border-destructive/40 bg-destructive/10">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <p className="font-serif italic text-sm text-muted-foreground">
        We use the details you submit to respond to your inquiry and prepare your quote. Read our{" "}
        <Link to="/PrivacyPolicyPage" className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
          Privacy Policy
        </Link>
        .
      </p>

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 md:h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-base tracking-wide shadow-md hover:shadow-lg transition-all">
        {isSubmitting ? "Sending..." : <>
            Request Consultation <ArrowRight className="ml-1 size-4" />
          </>}
      </Button>

      <Button asChild className="w-full h-12 md:h-14 bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default text-base tracking-wide">
        <Link to="tel:+15878884223">
          Call Us (587) 888-4223
        </Link>
      </Button>

      <p className="text-center font-serif italic text-sm text-muted-foreground pt-1">
        Our team will contact you within 24 hours to confirm your appointment.
      </p>
    </Form>;
}
export default function Transformation() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3578" id="start-your-transformation" className="bg-background text-foreground px-5 py-7 md:px-16 md:py-7">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div ref={leftRef} className={`lg:col-span-5 lg:sticky lg:top-24 self-start transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="uppercase tracking-[0.3em] text-xs font-default text-primary mb-10">
            Premium Exterior Services
          </p>

          <h2 className="font-default text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] text-foreground mb-8">
            Start Your
            <br />
            <em className="text-primary font-light">Transformation.</em>
          </h2>

          <p className="font-serif text-lg text-muted-foreground leading-relaxed mb-12 max-w-md">
            MetalLine Exteriors delivers precision-engineered roofing and cladding solutions for Calgary's most discerning homeowners. Begin your journey toward a durable, high-performance exterior today.
          </p>

          <div className="pt-8 border-t border-border">
            <p className="uppercase tracking-widest text-xs font-default text-muted-foreground mb-6">
              What to Expect
            </p>
            <ul className="space-y-3">
              {expectList.map((item, i) => <li key={item} data-index={i} className="font-serif italic text-base text-foreground flex gap-3">
                  <span className="text-primary not-italic">—</span>
                  <span>{item}</span>
                </li>)}
            </ul>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <p className="font-serif italic text-sm text-muted-foreground">
              METALLINE EXTERIORS · Calgary, AB
            </p>
            <p className="font-mono text-xs tracking-wider text-muted-foreground/80 mt-1">
              Serving Calgary, Airdrie, Okotoks, Cochrane & Chestermere
            </p>
          </div>
        </div>

        <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <FormProvider formKey="quote_request_form_home" formId="quote_request_form_home" sectionName="start-your-transformation" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: "",
          postal_code: "",
          project_type: "",
          project_description: "",
          preferred_date: "",
          preferred_time: "",
          supporting_document: ""
        }} validationRules={{
          full_name: {
            required: true
          },
          email_address: {
            pattern: "email"
          },
          phone_number: {
            required: true,
            pattern: "phone"
          },
          postal_code: {
            required: true
          },
          project_type: {
            required: true
          }
        }} submitText="Request Consultation" successMessage="Thank you! Your request has been submitted successfully. We will contact you shortly." errorMessage="We were unable to submit your request. Please try again or contact us directly." className="rounded-lg border border-border bg-card p-6 md:p-12 shadow-lg">
            <div className="mb-10">
              <p className="uppercase tracking-[0.3em] text-xs font-default text-primary mb-4">
                Project Inquiry
              </p>
              <p className="font-serif italic text-muted-foreground text-base">
                Provide your project details to receive a professional assessment.
              </p>
            </div>
            <ConsultationForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}